import { Skeleton } from '@/components/ui/skeleton';
import { PageWrapper, Container } from '@/components/layout';

/**
 * Skeleton for the Legislator Detail page.
 * Reserves ~590px total height to prevent CLS.
 *
 * Layout breakdown:
 * - Header with avatar and info: ~150px
 * - Contact card: ~100px
 * - Tabs: ~40px
 * - Tab content: ~300px
 */
export function LegislatorDetailPageSkeleton() {
  return (
    <PageWrapper customContainer={true}>
      {/* Custom Legislator Header */}
      <header className="w-full relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-y-0 w-[100vw] left-1/2 -translate-x-1/2 bg-gradient-to-r from-background via-background to-muted/30 dark:from-dark-base dark:via-dark-base dark:to-dark-elevated/50"
          aria-hidden="true"
        />
        <Container size="xl" padding className="relative z-10">
          <div className="py-4 lg:py-6">
            {/* Back button */}
            <Skeleton className="h-6 w-32 mb-3" />

            {/* Title and description */}
            <Skeleton className="h-9 w-64 mb-2" />
            <Skeleton className="h-5 w-80 mb-4" />

            {/* Avatar and actions */}
            <div className="flex items-center gap-4 mt-4">
              <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />
              <div className="flex-1 flex items-center gap-2">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-10 w-28" />
              </div>
            </div>
          </div>
        </Container>
      </header>

      <Container size="xl" padding>
        {/* Tabs */}
        <div className="border-b border-border mt-4">
          <div className="flex gap-4 -mb-px overflow-x-auto">
            {['Profile', 'Votes', 'Bills', 'Committees', 'History'].map((tab) => (
              <Skeleton key={tab} className="h-10 w-24 flex-shrink-0" />
            ))}
          </div>
        </div>

        {/* Tab Content - Profile */}
        <div className="py-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Contact Card */}
            <div className="md:col-span-1">
              <div className="bg-card border border-border rounded-lg p-4 space-y-4">
                <Skeleton className="h-5 w-28" />
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-4 w-36" />
                  </div>
                </div>
                <Skeleton className="h-10 w-full" />
              </div>
            </div>

            {/* Main Content */}
            <div className="md:col-span-2 space-y-6">
              {/* Bio Section */}
              <div className="bg-card border border-border rounded-lg p-4 space-y-3">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>

              {/* Committees Section */}
              <div className="bg-card border border-border rounded-lg p-4 space-y-3">
                <Skeleton className="h-5 w-32" />
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </PageWrapper>
  );
}
